import { plural } from "@/components/common/setUtils";
import { linkEndLabel } from "./draft-helpers";
import type {
  DraftColumn,
  DraftFeedback,
  DraftTable,
  DraftVersion,
} from "@/lib/api/apps/types";

/** The table diff view's marks: added, removed, modified. */
export type ChangeSign = "+" | "-" | "~";

/** One of the agent's moves between two versions: a sentence (`verb a mid b`),
 *  a mono detail, and a reason only where the harness knows it (a drop, a
 *  link that lost its table). */
export interface DraftChange {
  sign: ChangeSign;
  verb: string;
  a: string;
  mid?: string;
  b?: string;
  detail?: string;
  reason?: string;
  /** The table the change is in, still there to open. */
  tableId?: string;
}

const quote = (text: string) => `“${text}”`;

/** A column's identity in the server's comparison. */
export const columnKey = (c: DraftColumn) => c.id ?? `name:${c.name}`;

/** The server's changes from `prev` to `next` as the Changes list words them;
 *  `label` turns a type name into what the canvas calls it. */
export function describeChanges(
  prev: DraftVersion,
  next: DraftVersion,
  label: (dataType: string) => string,
): DraftChange[] {
  const tableIn = (v: DraftVersion, id: string) =>
    v.tables.find((t) => t.id === id);
  const tableOf = (id: string) => tableIn(next, id) ?? tableIn(prev, id);
  const nameOf = (id: string) => tableOf(id)?.name ?? id;
  const endOf = (id: string) => {
    const table = tableOf(id);
    return table ? linkEndLabel(table) : id;
  };
  const columnIn = (tables: DraftTable[], key: string) => {
    for (const t of tables) {
      const c = t.columns.find((x) => columnKey(x) === key);
      if (c) return c;
    }
    return undefined;
  };
  const marks = next.marks?.tables ?? {};

  const typeLine = (c: DraftColumn) =>
    [
      [label(c.data_type), c.unit].filter(Boolean).join(" "),
      c.key && "key",
      c.unique && "unique",
      c.required && "required",
      c.link_to_table && `→ ${endOf(c.link_to_table)}`,
    ]
      .filter(Boolean)
      .join(" · ");
  // A retype the canvas label would hide (Number → Number) names the storage.
  const retype = (from: string, to: string) =>
    label(from) === label(to)
      ? `${from} → ${to}`
      : `${label(from)} → ${label(to)}`;
  const unitOf = (x?: DraftColumn) =>
    x?.unit
      ? `${x.unit}${x.scale_exponent ? ` ×10^${x.scale_exponent}` : ""}`
      : "none";

  return (next.changes ?? []).map((ch): DraftChange => {
    const t = tableOf(ch.table);
    const tableName = nameOf(ch.table);
    const row = (
      sign: ChangeSign,
      verb: string,
      a: string,
      more: Partial<DraftChange> = {},
    ): DraftChange => ({
      tableId: tableIn(next, ch.table) ? ch.table : undefined,
      sign,
      verb,
      a,
      ...more,
      ...(ch.not_applied && { reason: `Not applied: ${ch.not_applied}` }),
    });
    if (ch.op === "remove_table")
      return row("-", "Remove table", tableName, {
        detail: plural(tableIn(prev, ch.table)?.columns.length ?? 0, "column"),
        reason:
          marks[ch.table]?.verdict === "drop"
            ? `You dropped ${tableName}`
            : undefined,
      });
    if (ch.op === "add_table") {
      const columns = t?.columns ?? [];
      return row("+", "Add table", tableName, {
        detail: `${plural(columns.length, "column")}: ${columns.map((c) => c.name).join(", ")}`,
      });
    }
    if (ch.op === "rename_table")
      return row("~", "Rename table", ch.from, { mid: "to", b: ch.to });
    if (ch.op === "describe_table")
      return row("~", "Describe", tableName, { detail: ch.to });
    const now = columnIn(next.tables, ch.column);
    const was = columnIn(prev.tables, ch.column);
    const path = `${tableName}.${now?.name ?? was?.name ?? ch.column}`;
    switch (ch.op) {
      case "add_column":
        return row("+", "Add column", now?.name ?? ch.column, {
          mid: "to",
          b: tableName,
          detail: now && typeLine(now),
        });
      case "remove_column":
        return row("-", "Remove column", was?.name ?? ch.column, {
          mid: "from",
          b: tableName,
          detail: was && typeLine(was),
          reason:
            was && marks[ch.table]?.drop_columns?.includes(was.name)
              ? "You dropped it"
              : undefined,
        });
      case "move_column":
        return row(
          "~",
          "Move column",
          `${nameOf(ch.from_table)}.${was?.name ?? ch.column}`,
          { mid: "to", b: tableName },
        );
      case "rename_column":
        return row("~", "Rename column", `${tableName}.${ch.from ?? ""}`, {
          mid: "to",
          b: ch.to ?? "",
        });
      case "retype":
        return row("~", "Change type of", path, {
          detail: retype(ch.from, ch.to),
        });
      case "set_unit":
        return row("~", "Change unit of", path, {
          detail: `${unitOf(was)} → ${unitOf(now)}`,
        });
      case "set_options":
        return row("~", "Change options of", path, {
          detail: [
            ...ch.added.map((x) => `+${x}`),
            ...ch.removed.map((x) => `−${x}`),
          ].join(" · "),
        });
      case "set_intent":
        return row("~", `${ch.on ? "Set" : "Clear"} ${ch.intent} on`, path);
      case "describe_column":
        return row("~", "Describe", path, { detail: ch.to });
      case "add_link":
        return row("+", "Add link", path, { mid: "→", b: endOf(ch.to_table) });
      case "remove_link":
        return row("-", "Remove link", path, {
          mid: "→",
          b: endOf(ch.to_table),
          reason: tableIn(next, ch.to_table)
            ? undefined
            : `${nameOf(ch.to_table)} is removed`,
        });
      case "move_link": {
        const before = tableIn(prev, ch.to_table);
        const after = tableIn(next, ch.to_table);
        return row("~", "Move link", path, {
          mid: "→",
          b: after ? linkEndLabel(after) : endOf(ch.to_table),
          detail: before ? `was ${linkEndLabel(before)}` : undefined,
          reason: `The key of ${nameOf(ch.to_table)} changed`,
        });
      }
    }
  });
}

/** The marks a round was sent with, one line each, as the drafting card
 *  lists them. */
/** One mark as a line; `tableId` is the table it is on, absent for the note to the whole draft. */
export type MarkLine = {
  kind: "keep" | "drop" | "drop-column" | "change" | "comment" | "note";
  sign?: ChangeSign;
  text: string;
  tableId?: string;
  tableName?: string;
};

export function markLines(
  marks: DraftFeedback,
  tables: DraftTable[],
): MarkLine[] {
  const name = (id: string) => tables.find((t) => t.id === id)?.name ?? id;
  const lines: MarkLine[] = [];
  for (const [id, f] of Object.entries(marks.tables)) {
    const note = f.note.trim();
    const on = { tableId: id, tableName: name(id) };
    if (f.verdict === "keep")
      lines.push({ ...on, kind: "keep", text: `Lock ${name(id)}` });
    if (f.verdict === "drop")
      lines.push({ ...on, kind: "drop", sign: "-", text: `Drop ${name(id)}` });
    if (f.verdict === "change" || note)
      lines.push({
        ...on,
        kind: "change",
        sign: "~",
        text: `Change ${name(id)}${note ? `: ${quote(note)}` : ""}`,
      });
    for (const column of f.drop_columns)
      lines.push({
        ...on,
        kind: "drop-column",
        sign: "-",
        text: `Drop ${name(id)}.${column}`,
      });
    for (const [column, text] of Object.entries(f.column_notes ?? {}))
      if (text.trim())
        lines.push({
          ...on,
          kind: "comment",
          sign: "~",
          text: `${name(id)}.${column}: ${quote(text.trim())}`,
        });
  }
  if (marks.note.trim())
    lines.push({ kind: "note", sign: "~", text: quote(marks.note.trim()) });
  return lines;
}

const andList = (xs: string[]) =>
  xs.length <= 1
    ? (xs[0] ?? "")
    : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
/** What a round asks of the agent, as a sentence or three: the head of its review naming tables while they are
 *  few, and with `counted` a shorter one for a hover. */
export function reviewProse(
  marks: DraftFeedback,
  tables: DraftTable[],
  { sent = false, counted = false } = {},
): string {
  const namesOr = (names: string[], noun: string) =>
    !counted && names.length <= 3 ? andList(names) : plural(names.length, noun);
  const entries = Object.entries(marks.tables);
  const named = (has: (f: (typeof entries)[number][1]) => boolean) =>
    entries
      .filter(([, f]) => has(f))
      .map(([id]) => tables.find((t) => t.id === id)?.name ?? id);
  const changed = named((f) => f.verdict === "change" || !!f.note.trim());
  const dropped = named((f) => f.verdict === "drop");
  const kept = named((f) => f.verdict === "keep");
  const columns = entries.reduce((n, [, f]) => n + f.drop_columns.length, 0);
  const comments = entries.reduce(
    (n, [, f]) =>
      n + Object.values(f.column_notes ?? {}).filter((t) => t.trim()).length,
    0,
  );
  const asks = [
    changed.length > 0 && `change ${namesOr(changed, "table")}`,
    dropped.length > 0 && `drop ${namesOr(dropped, "table")}`,
    columns > 0 && `remove ${plural(columns, "column")}`,
    comments > 0 && `answer ${plural(comments, "comment")} on columns`,
  ].filter((x): x is string => !!x);
  const one = kept.length === 1;
  const said = [
    asks.length > 0 &&
      `${sent ? "You asked" : "You're asking"} the agent to ${andList(asks)}.`,
    kept.length > 0 &&
      `${namesOr(kept, "table")} ${sent ? (one ? "was locked" : "were locked") : "will be locked"}.`,
  ].filter((x): x is string => !!x);
  if (marks.note.trim())
    said.push(
      said.length
        ? `${sent ? "Your note went" : "Your note goes"} with it.`
        : `${sent ? "You sent" : "You're sending"} the agent a note.`,
    );
  return (
    said.join(" ") || (sent ? "Nothing was marked." : "Nothing marked yet.")
  );
}
